import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface OnboardingTooltipProps {
  targetRef: React.RefObject<HTMLElement | null>;
  title: string;
  message: string;
  onDismiss: () => void;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

interface Position {
  top: number;
  left: number;
  actualPlacement: 'top' | 'bottom' | 'left' | 'right';
}

const TOOLTIP_MAX_WIDTH = 280;
const ARROW_SIZE = 8;
const GAP = 10;

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  targetRef,
  title,
  message,
  onDismiss,
  placement = 'auto',
}) => {
  const [position, setPosition] = useState<Position | null>(null);
  const [visible, setVisible] = useState(false);

  const calculatePosition = useCallback(() => {
    const target = targetRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Estimate tooltip height (title + message + button + padding)
    const tooltipHeight = 120;
    const tooltipWidth = Math.min(TOOLTIP_MAX_WIDTH, viewportW - 20);

    let actualPlacement = placement;
    if (actualPlacement === 'auto') {
      // Prefer bottom, fall back to top, then right, then left
      const spaceBelow = viewportH - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportW - rect.right;
      const spaceLeft = rect.left;

      if (spaceBelow >= tooltipHeight + GAP + ARROW_SIZE) {
        actualPlacement = 'bottom';
      } else if (spaceAbove >= tooltipHeight + GAP + ARROW_SIZE) {
        actualPlacement = 'top';
      } else if (spaceRight >= tooltipWidth + GAP + ARROW_SIZE) {
        actualPlacement = 'right';
      } else if (spaceLeft >= tooltipWidth + GAP + ARROW_SIZE) {
        actualPlacement = 'left';
      } else {
        actualPlacement = 'bottom'; // fallback
      }
    }

    let top = 0;
    let left = 0;

    switch (actualPlacement) {
      case 'bottom':
        top = rect.bottom + GAP + ARROW_SIZE + window.scrollY;
        left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
        break;
      case 'top':
        top = rect.top - tooltipHeight - GAP - ARROW_SIZE + window.scrollY;
        left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
        left = rect.right + GAP + ARROW_SIZE + window.scrollX;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
        left = rect.left - tooltipWidth - GAP - ARROW_SIZE + window.scrollX;
        break;
    }

    // Clamp horizontal position to viewport
    left = Math.max(10, Math.min(left, viewportW - tooltipWidth - 10 + window.scrollX));

    setPosition({ top, left, actualPlacement });
  }, [targetRef, placement]);

  useEffect(() => {
    calculatePosition();
    // Trigger fade-in after position is set
    const fadeTimer = requestAnimationFrame(() => setVisible(true));

    const handleResize = () => calculatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      cancelAnimationFrame(fadeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [calculatePosition]);

  if (!position) return null;

  const arrowStyle = getArrowStyle(position.actualPlacement);

  return createPortal(
    <div
      className="onboarding-tooltip"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        maxWidth: TOOLTIP_MAX_WIDTH,
        backgroundColor: '#fff',
        borderRadius: 10,
        padding: '16px 20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(4px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
      }}
    >
      <div style={arrowStyle} />
      <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
        {message}
      </div>
      <button
        onClick={onDismiss}
        style={{
          backgroundColor: 'var(--primary-color, #4a90d9)',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          padding: '6px 16px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Got it
      </button>
    </div>,
    document.body
  );
};

function getArrowStyle(placement: 'top' | 'bottom' | 'left' | 'right'): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  switch (placement) {
    case 'bottom':
      return {
        ...base,
        top: -ARROW_SIZE,
        left: '50%',
        marginLeft: -ARROW_SIZE,
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid #fff`,
      };
    case 'top':
      return {
        ...base,
        bottom: -ARROW_SIZE,
        left: '50%',
        marginLeft: -ARROW_SIZE,
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderTop: `${ARROW_SIZE}px solid #fff`,
      };
    case 'right':
      return {
        ...base,
        left: -ARROW_SIZE,
        top: '50%',
        marginTop: -ARROW_SIZE,
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid #fff`,
      };
    case 'left':
      return {
        ...base,
        right: -ARROW_SIZE,
        top: '50%',
        marginTop: -ARROW_SIZE,
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderLeft: `${ARROW_SIZE}px solid #fff`,
      };
  }
}

export default OnboardingTooltip;
